import React, { useCallback, useMemo, useState } from 'react';
import { FaLinkedin } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { MdOutlineEmail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { RiKey2Line } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import usePageRendered from '../../hooks/usePageRendered';
import { HEADERMENU_PATH } from '../../routes/RoutesURL';
import HSButton from '../common/Button';
import InputToolTip from '../common/InputTooltip';
import NormalInput from '../common/NormalInput';
import messageActions from '../message/redux/actions';
import { TermAndConditionOnLanding } from '../signUp/SignUp';
import { federatedActions } from '../socialLogin';
import { emailValidation } from '../utils/validation';
import actions from './redux/actions';
import { User, UserState } from './redux/types';

export const socialRedirectURL = `/user/federated/google/redirect`;
export const socialLinkedinRedurectURL = `/user/federated/linkedin/redirect`;

export type LoginResponse = {
    uuid?: string;
    jwtToken?: string;
    user?: User;
};

const socialLoginStyle = {
    borderRadius: 99999,
    paddingRight: '2rem',
    marginTop: '1rem',
    paddingLeft: '2rem',
    backgroundColor: '#fff',
    color: '#20222B',
    borderColor: '#20222B',
};

const Login: React.FC = () => {
    console.log('login page');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const url_key = searchParams.get('url_key');

    const [formFields, setFormFields] = useState(getDefaultFormFields());
    const [isVisibility, setVisibility] = useState<boolean>(false);
    const [isValidate, setValidate] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const pageTransitionCss = usePageRendered();

    const postDataObj = useMemo(() => {
        let postData: any = {};
        formFields.forEach((ff) => {
            postData[ff.key] = ff.value;
        });
        return postData;
    }, [formFields]);

    const onClickLogin = useCallback(() => {
        setValidate(true);
        if (!emailValidation(postDataObj.email) || !postDataObj.password || postDataObj.password === '') {
            return;
        }
        setValidate(false);
        setLoading(true);
        api.login(postDataObj.email.trim(), postDataObj.password)
            .then((res) => {
                const response: LoginResponse = res.data;
                const { jwtToken, user } = response;

                const userData: UserState = {
                    jwtToken,
                    user,
                    uuid: user?.uuid,
                };

                dispatch(actions.loginReq(userData, url_key !== null ? url_key : undefined));
            })
            .catch((err) => {
                setLoading(false);
                dispatch(messageActions.showMessage({ error: true, text: String(err) }));
            })
            .finally(() => {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            });
    }, [dispatch, postDataObj, url_key]);

    const form = useMemo(() => {
        const onFieldChange = (data: { name: string; value?: number | string }) => {
            const { name, value } = data;
            let newFormFields = formFields.slice();
            let index = newFormFields.findIndex((f) => f.key === name);
            if (index >= 0) {
                newFormFields[index] = {
                    ...newFormFields[index],
                    value: name === 'email' ? (value ? String(value).toLowerCase() : value) : value,
                };
                setFormFields(newFormFields);
            }
        };

        const isError = (key: string, value: FormFieldValue) => {
            if (!isValidate) {
                return {
                    isError: false,
                };
            }
            if (key === 'email') {
                return {
                    isError: !emailValidation(String(value)),
                    message: 'Please enter valid email address',
                };
            }
            if (key === 'password') {
                return {
                    isError: value && String(value).length > 0 ? false : true,
                    message: 'Please enter password',
                };
            }
            return {
                isError: false,
            };
        };

        return (
            <>
                {formFields.map((f) => {
                    if (['input'].includes(f.inputType)) {
                        return (
                            <div className={`border ${f.key === 'email' ? 'border-bottom-0' : ''}`} key={f.key}>
                                <InputToolTip title={f.title}>
                                    <NormalInput
                                        placeholder={f.title}
                                        type={f.key === 'password' ? (isVisibility ? 'text' : f.type) : f.type}
                                        name={f.key}
                                        onChange={onFieldChange}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                onClickLogin();
                                            }
                                        }}
                                        value={f.value}
                                        isRequired={f.required}
                                        error={isError(f.key, f.value).isError}
                                        bordered={false}
                                        prefix={
                                            f.key === 'email' ? (
                                                <MdOutlineEmail color="rgb(196, 196, 196)" size={'1rem'} />
                                            ) : (
                                                <RiKey2Line color="rgb(196, 196, 196)" size={'1rem'} />
                                            )
                                        }
                                        suffix={
                                            f.key === 'password' &&
                                            (isVisibility ? (
                                                <MdVisibility
                                                    className="cursor-pointer"
                                                    size="1.2rem"
                                                    onClick={() => setVisibility(!isVisibility)}
                                                />
                                            ) : (
                                                <MdVisibilityOff
                                                    className="cursor-pointer"
                                                    size="1.2rem"
                                                    onClick={() => setVisibility(!isVisibility)}
                                                />
                                            ))
                                        }
                                    />
                                    {isError(f.key, f.value).isError && (
                                        <div className="common_error px-3">{isError(f.key, f.value).message}</div>
                                    )}
                                </InputToolTip>
                            </div>
                        );
                    } else {
                        return null;
                    }
                })}
            </>
        );
    }, [formFields, isVisibility, isValidate, onClickLogin]);

    return (
        <div className={`h-full flex justify-center items-center ${pageTransitionCss}`}>
            <div className="w-full" id="login">
                <div>
                    {form}
                    <div className="flex justify-end font-[Lato]">
                        <span
                            className="text-sm text-[#1B3E8C] cursor-pointer"
                            onClick={() => navigate(HEADERMENU_PATH.forgotPassword)}
                        >
                            Forgot your password?
                        </span>
                    </div>
                    <div className="mt-4 flex">
                        <TermAndConditionOnLanding />
                    </div>
                    <div className="flex justify-center items-center m-4 pt-6 mt-2 mb-2">
                        <HSButton
                            title="Log In"
                            loading={loading}
                            onClick={onClickLogin}
                            style={{ borderRadius: 0, paddingRight: '2rem', paddingLeft: '2rem' }}
                        />
                    </div>
                    {process.env.REACT_APP_SOCAIL_LOGIN_ENABLE === 'yes' && (
                        <>
                            <div className="relative flex py-5 items-center">
                                <div className="flex-grow border-t border-[#C4CFE4]"></div>
                                <span className="flex-shrink mx-4 text-[#404040] font-[WorkSans]">OR</span>
                                <div className="flex-grow border-t border-[#C4CFE4]"></div>
                            </div>

                            <div className="flex justify-center items-center m-4 mt-2 mb-4 flex-col social_login">
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${socialRedirectURL}?action=${federatedActions.login}`}
                                >
                                    <HSButton
                                        title="Login with Google"
                                        light
                                        icon={<FcGoogle />}
                                        style={{ ...socialLoginStyle }}
                                    />
                                </a>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${socialLinkedinRedurectURL}?action=${federatedActions.login}`}
                                >
                                    <HSButton
                                        title="Login with Linkedin"
                                        light
                                        icon={<FaLinkedin color="#2464ad" />}
                                        style={{ ...socialLoginStyle, marginTop: '1rem' }}
                                    />
                                </a>
                            </div>
                        </>
                    )}
                    {/* <p className="flex text-base justify-center items-center">
                        New to Helicap Securities?
                        <HSButton
                            title="Sign Up"
                            type="link"
                            onClick={() => history.push(HEADERMENU_PATH.signUp)}
                            className="text-base text-blue-500 pl-0"
                        />
                    </p> */}
                </div>
            </div>
        </div>
    );
};

export default Login;

type InputType = 'input';
export type FormFieldValue = string | number | null | undefined;
interface LoginForm {
    email: string;
    password: string;
}
export interface FormField {
    key: keyof LoginForm;
    title: string;
    inputType: InputType;
    type?: 'text' | 'password';
    value: FormFieldValue;
    index: number;
    required?: boolean;
    disabled?: boolean;
}

const getDefaultFormFields = (): FormField[] => {
    return [
        {
            key: 'email',
            title: 'Email',
            inputType: 'input',
            index: 0,
            value: undefined,
            required: true,
        },
        {
            key: 'password',
            title: 'Password',
            inputType: 'input',
            index: 0,
            type: 'password',
            value: undefined,
            required: true,
        },
    ];
};
